// AddCompany.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Eye, X } from 'lucide-react';
import './Company.css';

const url = process.env.REACT_APP_BACKEND_URL;

const AddCompany = () => {
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    companyname: "",
    acronym: "",
    companytype: "",
    currentmanager: "",
    contact: "",
    companylocation: "",
    companylogo: null,
    companyseal: null,
    companysignature: null,
  });
  
  const [preview, setPreview] = useState({
    companylogo: "",
    companyseal: "",
    companysignature: ""
  });
  
  const [focusedFields, setFocusedFields] = useState({});
  const navigate = useNavigate();

  const handleFocus = (field) => {
    setFocusedFields(prev => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setFocusedFields(prev => ({
      ...prev,
      [field]: !!data[field],
    }));
  };

  const onChangeHandler = (event) => {
    const { name, value, files } = event.target;
    
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`Please select an image file for ${name.replace('company', '').replace(/([A-Z])/g, ' $1')}`);
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`File size should be less than 2MB for ${name.replace('company', '').replace(/([A-Z])/g, ' $1')}`);
        return;
      }
      
      setData(prev => ({ ...prev, [name]: file }));
      setPreview(prev => ({ ...prev, [name]: URL.createObjectURL(file) }));
    } else {
      setData(prev => ({ ...prev, [name]: value }));
    }
  };

  const removeFile = (field) => {
    setData(prev => ({ ...prev, [field]: null }));
    setPreview(prev => ({ ...prev, [field]: "" }));
    
    // Clear the file input
    const fileInput = document.getElementById(field);
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const onSubmitHandler = async (event) => {
    event.preventDefault();

    if (!data.companyname.trim() || !data.companytype.trim()) {
      toast.error("Company name and type are required");
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (data[key] !== null && data[key] !== "") {
          formData.append(key, data[key]);
        }
      });

      const response = await axios.post(
        `${url}/api/company`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.success) {
        toast.success("Company added successfully!");
        setData({
          companyname: "",
          acronym: "",
          companytype: "",
          currentmanager: "",
          contact: "",
          companylocation: "",
          companylogo: null,
          companyseal: null,
          companysignature: null,
        });
        setPreview({
          companylogo: "",
          companyseal: "",
          companysignature: ""
        });
        navigate("/ViewCompany");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to add company";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-wrapper">
      <div className="company-form-container">
        <h2 className="form-title">
          <Building2 size={20} className="inline mr-2" /> Add New Company
        </h2>
        <div className="header">
          <button 
            onClick={() => navigate("/ViewCompany")}
            className="add-button"
          >
            <Plus size={16} /> View Companies
          </button>
        </div>

        <form className="company-form" onSubmit={onSubmitHandler} encType="multipart/form-data">
          {/* Text Fields */}
          {["companyname", "acronym", "companytype", "currentmanager", "contact", "companylocation"].map((field) => (
            <div key={field} className={`form-field ${focusedFields[field] || data[field] ? "filled" : ""}`}>
              <input
                type="text"
                name={field}
                value={data[field]}
                onChange={onChangeHandler}
                onFocus={() => handleFocus(field)}
                onBlur={() => handleBlur(field)}
                required={field === "companyname" || field === "companytype"}
              />
              <label>
                {field === "companyname" ? "Company Name" :
                 field === "acronym" ? "Acronym" :
                 field === "companytype" ? "Company Type" :
                 field === "currentmanager" ? "Current Manager" :
                 field === "contact" ? "Contact" :
                 "Company Location"}
                {field === "companyname" || field === "companytype" ? " *" : ""}
              </label>
            </div>
          ))}

          {/* Company Logo Upload */}
          <div className="form-field file-upload-field">
            <label>Company Logo</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="companylogo"
                name="companylogo"
                accept="image/*"
                onChange={onChangeHandler}
                className="file-input"
              />
              <button type="button" className="file-input-button" onClick={() => document.getElementById('companylogo').click()}>
                <Eye size={16} /> Choose Logo
              </button>
              <span className="file-name">
                {data.companylogo ? data.companylogo.name : "No file chosen"}
              </span>
            </div>
            {preview.companylogo && (
              <div className="image-preview">
                <img src={preview.companylogo} alt="Logo Preview" />
                <button
                  type="button"
                  className="remove-image"
                  onClick={() => removeFile('companylogo')}
                >
                  <X size={14} /> Remove
                </button>
              </div>
            )}
          </div>

          {/* Company Seal Upload */}
          <div className="form-field file-upload-field">
            <label>Company Seal</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="companyseal"
                name="companyseal"
                accept="image/*"
                onChange={onChangeHandler}
                className="file-input"
              />
              <button type="button" className="file-input-button" onClick={() => document.getElementById('companyseal').click()}>
                <Eye size={16} /> Choose Seal
              </button>
              <span className="file-name">
                {data.companyseal ? data.companyseal.name : "No file chosen"}
              </span>
            </div>
            {preview.companyseal && (
              <div className="image-preview">
                <img src={preview.companyseal} alt="Seal Preview" />
                <button
                  type="button"
                  className="remove-image"
                  onClick={() => removeFile('companyseal')}
                >
                  <X size={14} /> Remove
                </button>
              </div>
            )}
          </div>

          {/* Company Signature Upload */}
          <div className="form-field file-upload-field">
            <label>Company Signature</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="companysignature"
                name="companysignature"
                accept="image/*"
                onChange={onChangeHandler}
                className="file-input"
              />
              <button type="button" className="file-input-button" onClick={() => document.getElementById('companysignature').click()}>
                <Eye size={16} /> Choose Signature
              </button>
              <span className="file-name">
                {data.companysignature ? data.companysignature.name : "No file chosen"}
              </span>
            </div>
            {preview.companysignature && (
              <div className="image-preview">
                <img src={preview.companysignature} alt="Signature Preview" />
                <button
                  type="button"
                  className="remove-image"
                  onClick={() => removeFile('companysignature')}
                >
                  <X size={14} /> Remove
                </button>
              </div>
            )}
          </div>

          <div className="button-group">
            <button
              type="button"
              className="cancel-btn"
              onClick={() => navigate('/ViewCompany')}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={submitting}
            >
              {submitting ? "Processing..." : "Add Company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCompany;